import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost, apiDelete, formatDateTime, formatNumber } from "@/lib/api";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import EmptyState from "@/components/common/EmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText, Trash2, Download, Eye, Sparkles, RefreshCcw, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import { CompanyLink } from "@/components/common/StockLinks";
import { useAuth } from "@/lib/auth";
import ReportUploadForm from "@/components/common/ReportUploadForm";
import ReportAnalysisBanner from "@/components/common/ReportAnalysisBanner";
import AdminBulkReportsCard from "@/components/admin/AdminBulkReportsCard";
import useSmartPoll from "@/hooks/useSmartPoll";

const ReportStatusPill = ({ report }) => {
  const status = (report.analysis_status || "").toLowerCase();
  const isFailed = status === "failed" || status === "error";
  const isAnalysing = status === "analysing";
  const isPending = status === "pending";
  // Strict definition of "Analysed" — needs the live status to be
  // `complete|analysed`. The legacy `is_analysed` boolean alone is
  // unreliable: the failure handler also flips it to True, which used
  // to make a just-retried row briefly render as "Analysed" with no
  // content (Fix 1, Apr 30 incident).
  const isAnalysed = (status === "complete" || status === "analysed") && !isFailed && !isAnalysing;
  if (isFailed) {
    return (
      <Badge
        variant="outline"
        className="bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30 text-[10px] gap-1"
        title={report.analysis_error || ""}
        data-testid={`report-status-${report.report_id}-failed`}
      >
        <AlertTriangle className="h-2.5 w-2.5" /> Failed
      </Badge>
    );
  }
  if (isAnalysing) {
    return (
      <Badge variant="outline" className="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/30 text-[10px] gap-1" data-testid={`report-status-${report.report_id}-analysing`}>
        <Loader2 className="h-2.5 w-2.5 animate-spin" /> Analysing
      </Badge>
    );
  }
  if (isAnalysed) {
    return (
      <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 text-[10px]" data-testid={`report-status-${report.report_id}-analysed`}>
        Analysed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 text-[10px]" data-testid={`report-status-${report.report_id}-pending`}>
      {isPending ? "Pending" : "Pending"}
    </Badge>
  );
};

const ReportsPage = () => {
  const { isAdmin } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // { report_id, title }
  const [deleting, setDeleting] = useState(false);

  const load = async ({ background = false } = {}) => {
    // Background polls don't flip the loading skeleton — only the
    // first load does. This is the key fix for the 10-second
    // visible flicker the user reported: every poll tick was
    // toggling `loading` on then off, which caused the entire page
    // to re-render its layout.
    try {
      const rs = await apiGet("/reports");
      const next = Array.isArray(rs) ? rs : [];
      // Diff-aware update — only call setReports when something
      // actually changed. Uses a stringified fingerprint of the
      // fields we care about. When nothing changed we return prev
      // from the updater so React bails out of re-rendering
      // entirely.
      setReports((prev) => {
        if (!Array.isArray(prev) || prev.length !== next.length) return next;
        const fingerprint = (r) => `${r.report_id}|${r.analysis_status || ""}|${r.is_analysed ? 1 : 0}|${r.analysis_complete ? 1 : 0}|${r.analysis_error || ""}|${r.size_bytes || 0}`;
        const prevSig = prev.map(fingerprint).join("##");
        const nextSig = next.map(fingerprint).join("##");
        return prevSig === nextSig ? prev : next;
      });
      setError(null);
    } catch (e) {
      setError((prev) => prev || e.message || "Failed to load reports");
      throw e;
    }
  };

  useEffect(() => {
    setLoading(true);
    load().catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Fix 3 (round 2) — smart polling. Polls every 15 s, only while
  // there are rows in `analysing` or `pending` state, and applies a
  // 30 s backoff on failure. Polling stops completely when all rows
  // reach a terminal state (complete | failed) — it only resumes
  // when the user uploads a new report or clicks Retry.
  const hasInFlightAnalysis = (reports || []).some((r) => {
    const s = (r.analysis_status || "").toLowerCase();
    return s === "analysing" || s === "pending";
  });
  useSmartPoll({
    fetch: () => load({ background: true }),
    shouldPoll: () => hasInFlightAnalysis,
    intervalMs: 15_000,
    errorBackoffMs: 30_000,
    enabled: hasInFlightAnalysis,
  });

  const setBusy = (id, on) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };

  const onAnalyse = async (report) => {
    setBusy(report.report_id, true);
    try {
      await apiPost(`/reports/${report.report_id}/analyse`, {});
      toast.success("Analysis queued");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setBusy(report.report_id, false);
    }
  };

  // Fix 1 — admin Retry for failed reports. Uses force=true so the
  // backend bypasses the analysis_complete short-circuit.
  const onRetry = async (report) => {
    setBusy(report.report_id, true);
    try {
      await apiPost(`/reports/${report.report_id}/analyse?force=true`, {});
      toast.success("Retry queued — Claude is re-analysing the report");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(report.report_id, false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/reports/${deleteTarget.report_id}`);
      toast.success("Report deleted");
      setReports((prev) => prev.filter((r) => r.report_id !== deleteTarget.report_id));
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Indicator 2 — analysis-in-progress banner (hides itself when
          there are no analysing / pending rows). */}
      <ReportAnalysisBanner reports={reports} />

      {/* Admin Bulk Download & Analysis — mounted at the very top so admins
          see it before the per-row Library. Lets them multi-pick companies
          and fire one of 3 separated actions (download / analyse / both). */}
      {isAdmin && <AdminBulkReportsCard />}

      {/* Upload card — admin only. Regular users still browse and
          download reports from the Library below but cannot upload
          new files (security gate also enforced on the backend). */}
      {isAdmin && (
        <Card className="border bg-card">
          <CardHeader>
            <CardTitle className="font-display">Upload company report</CardTitle>
            <p className="text-xs text-muted-foreground">
              Pick a company, fill in the details, then choose a PDF (max 25 MB).
              Claude analyses the report automatically once uploaded.
            </p>
          </CardHeader>
          <CardContent>
            <ReportUploadForm onUploaded={() => load()} />
          </CardContent>
        </Card>
      )}

      <Card className="border bg-card overflow-hidden">
        <CardHeader>
          <CardTitle className="font-display">Library</CardTitle>
          <p className="text-xs text-muted-foreground">
            {reports.length} report{reports.length === 1 ? "" : "s"} stored
          </p>
        </CardHeader>
        <CardContent>
          {reports.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No reports yet"
              description="Upload an annual or interim PDF above to start your library."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-library-table">
                <thead className="bg-card sticky top-0">
                  <tr className="border-b">
                    <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Company</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">File</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Type</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Period</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Status</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Uploaded</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Size</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => {
                    const status = (r.analysis_status || "").toLowerCase();
                    const isFailed = status === "failed" || status === "error";
                    const isAnalysing = status === "analysing";
                    const isPending = status === "pending";
                    const isAnalysed = (status === "complete" || status === "analysed") && !isFailed && !isAnalysing;
                    const isBusy = busyIds.has(r.report_id);
                    return (
                      <tr key={r.report_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`reports-row-${r.report_id}`}>
                        <td className="px-4 py-3">
                          <CompanyLink
                            companyCode={r.company_code}
                            name={r.company_name}
                            fromLabel="Reports"
                            testId={`report-name-${r.report_id}`}
                          />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{r.file_name}</td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className="capitalize">{r.report_type}</Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{r.report_period || "—"}</td>
                        <td className="px-4 py-3"><ReportStatusPill report={r} /></td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDateTime(r.created_at)}</td>
                        <td className="px-4 py-3 text-right tabular">
                          {formatNumber((r.size_bytes || 0) / 1024, 0)} KB
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {(isAnalysing || isPending) && (
                              <span
                                className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--info))]"
                                data-testid={`report-busy-${r.report_id}`}
                              >
                                <Loader2 className="h-3 w-3 animate-spin" />
                                {isAnalysing ? "Analysing\u2026" : "Queued\u2026"}
                              </span>
                            )}
                            {isAnalysed && (
                              <Button asChild size="sm" variant="outline" className="gap-1.5" data-testid={`report-view-${r.report_id}`}>
                                <Link to={`/stocks/${r.company_code}?tab=reports`}>
                                  <Eye className="h-3.5 w-3.5" /> View
                                </Link>
                              </Button>
                            )}
                            {isAdmin && isFailed && (
                              <Button
                                size="sm"
                                onClick={() => onRetry(r)}
                                disabled={isBusy}
                                className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid={`report-retry-${r.report_id}`}
                                title={r.analysis_error || "Retry analysis"}
                              >
                                <RefreshCcw className="h-3.5 w-3.5" />
                                {isBusy ? "Retrying…" : "Retry"}
                              </Button>
                            )}
                            {isAdmin && !isAnalysed && !isFailed && !isAnalysing && !isPending && (
                              <Button
                                size="sm"
                                onClick={() => onAnalyse(r)}
                                disabled={isBusy}
                                className="gap-1.5"
                                data-testid={`report-reanalyse-${r.report_id}`}
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                {isBusy ? "Analysing…" : "Analyse"}
                              </Button>
                            )}
                            {isAdmin && isAnalysed && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRetry(r)}
                                disabled={isBusy}
                                className="gap-1.5"
                                data-testid={`report-reanalyse-${r.report_id}`}
                                title="Re-run Claude analysis"
                              >
                                <RefreshCcw className="h-3.5 w-3.5" />
                                Re-analyse
                              </Button>
                            )}
                            <a
                              href={`${API_BASE}/reports/file/${r.report_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                              aria-label="Download"
                              data-testid={`report-download-${r.report_id}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </a>
                            {isAdmin && (
                              <button
                                onClick={() => setDeleteTarget({
                                  report_id: r.report_id,
                                  title: r.file_name || r.title || r.report_period || "Report",
                                  period: r.report_period || "",
                                })}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-destructive/10 text-destructive"
                                aria-label="Delete"
                                data-testid={`report-delete-${r.report_id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog (admin only). */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent data-testid="report-delete-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" /> Delete this report?
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.title}
              {deleteTarget?.period ? ` · ${deleteTarget.period}` : ""}
              <br />
              This cannot be undone. The PDF will be permanently removed and any
              extracted analysis data will be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={deleting} onClick={() => setDeleteTarget(null)} data-testid="report-delete-cancel">
              Cancel
            </Button>
            <Button variant="destructive" disabled={deleting} onClick={confirmDelete} data-testid="report-delete-confirm" className="gap-2">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
